import { twMerge } from "tailwind-merge";
import { SVGs } from "../../assets";
import { Button, Text, Tooltip } from "../../components";
import type { TableColumn } from "../../components/Table";
import type { BookingPartnerLocation, Product } from "../../apis/products";
import type { AvailableService } from "../../apis/auth";
import { Tools } from "../../utils";
import { SystemServiceCode } from "../../stores";

export function getDashboardColumns({
  t,
  navigateToBooking,
  availableServices = [],
}: {
  t: (key: string, options?: Record<string, unknown>) => string;
  navigateToBooking: (data: Product) => void;
  availableServices?: AvailableService[];
}) {
  return [
    {
      key: "rank",
      title: t("general.rank"),
      className: "w-[75px]",
      classNames: { cell: "px-[0px]" },
      render: (_value: string, _: unknown, meta: { expanded: boolean }) => (
        <div
          className={twMerge(
            "inline-flex w-[75px] h-[70px] items-center justify-center",
            meta.expanded ? "bg-secondary" : null,
          )}
        >
          <Text
            level={5}
            children={String((meta as any).index + 1)}
            className={
              meta.expanded ? "text-text-surface" : "text-text-primary"
            }
          />
        </div>
      ),
    },
    {
      key: "partner_location",
      title: t("general.clinicName"),
      className: "w-[324px]",
      render: (loc: BookingPartnerLocation, r: Product) => {
        const nearby = loc?.nearbyPlaces?.[0]?.name;
        const street = (loc?.streetAddress ?? "").trim();
        const hint =
          {
            [SystemServiceCode.telehealth]: t(
              "dashboard.locationHint.telehealth",
            ),
            [SystemServiceCode.doctorHouseVisit]:
              street &&
              t("dashboard.locationHint.homeVisit", { address: street }),
          }[r.partner_service?.service?.code] ||
          (nearby && `${t("general.nearby")} ${nearby}`) ||
          loc?.city?.name ||
          street ||
          "";
        return (
          <div className="flex items-center justify-between gap-[10px] py-[4px]">
            <div className="flex flex-col flex-1">
              <Text>{loc?.code ?? "--"}</Text>
              {hint ? <Text level={5}>{hint}</Text> : null}
            </div>
            <Tooltip
              content={r?.description ?? t("general.noDescriptionAvailable")}
            >
              <img
                className="h-[19px] w-[19px]"
                src={SVGs.infoRoundFilled}
                alt="info"
              />
            </Tooltip>
          </div>
        );
      },
    },
    {
      key: "totalAdvancePrice",
      className: "w-[147px]",
      title: t("general.publicPrice"),
      render: (price: number = 0) => <Text>{Tools.formatEur(price)}</Text>,
    },
    {
      key: "totalAdvanceDiscountPrice",
      title: t("general.yourPrice"),
      className: "w-[180px]",
      render: (to: number = 0, r: Product) => (
        <div className="flex items-center gap-[10px]">
          <Text>{Tools.formatEur(to || r.totalAdvancePrice)}</Text>
          {!!to && (
            <Text className="text-critical">
              {`-${Tools.formatEur(r.totalAdvancePrice - to)} ${t("general.saving")}`}
            </Text>
          )}
        </div>
      ),
    },
    {
      key: "slots",
      title: t("general.nextAvailable"),
      className: "w-[180px]",
      render: (slots: string[] = [], r: Product) => {
        const next = slots[0]
          ? Tools.formatSmartDate(slots[0], r.partner_location?.timeZone)
          : "--";
        return (
          <div className="cursor-pointer" onClick={() => navigateToBooking(r)}>
            <Text className="hover:underline">{next}</Text>
            <Text
              level={5}
              className="hover:underline"
            >{`${slots.length} slots available`}</Text>
          </div>
        );
      },
    },
    {
      key: "partner_location.distance",
      title: t("general.distance"),
      className: "w-[153px]",
      render: (distance: number = 0) => (
        <Text children={Tools.formatKm(distance)} />
      ),
    },
    {
      key: "partner_location.availableServices",
      title: t("general.servicesAvailable"),
      className: "w-[184px]",
      render: (services: string[]) =>
        !services?.length ? (
          <Text>{"--"}</Text>
        ) : (
          <div
            className="flex items-center gap-[12px]"
            children={services.slice(0, 4).map((type, index) => {
              const meta = availableServices?.find((s) => s.id === type);
              const isMore = index === 3;

              if (!isMore && !meta) return null;
              const { iconUrl, name } = meta ?? {};
              const label = name;
              return (
                <Tooltip
                  key={index}
                  align="center"
                  className="inline-block"
                  content={isMore ? t("general.moreServicesAvailable") : label}
                >
                  {isMore ? (
                    <img
                      src={SVGs.close}
                      alt="plus"
                      className="h-[10px] w-[10px] rotate-45"
                    />
                  ) : iconUrl ? (
                    <img
                      src={iconUrl}
                      alt={label ?? ""}
                      className="h-[24px] w-[24px]"
                    />
                  ) : (
                    <Text level={5}>{label ?? ""}</Text>
                  )}
                </Tooltip>
              );
            })}
          />
        ),
    },
    {
      key: "assignedTo",
      title: t("general.assignedTo"),
      className: "w-[175px]",
      render: (_: unknown, r: Product) => (
        <Button color="secondary" onClick={() => navigateToBooking(r)}>
          {t("dashboard.bookNow")}
        </Button>
      ),
    },
  ] as TableColumn<Product>[];
}
